'use client';

import { Typography } from '@mui/material';
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

import { Loader } from '@/components/layout/loading';
import { useDAL } from '@/contexts/useDAL';

interface WorkspaceContextType {
  workspaceId: string | null;
  workspaceName: string | null;
  workspaceCloud: string | null;
  workspaceRegion: string | null;
  loading: boolean;
}

const WorkspaceContext = createContext<WorkspaceContextType>({
  workspaceId: null,
  workspaceName: null,
  workspaceCloud: null,
  workspaceRegion: null,
  loading: true,
});

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const dal = useDAL();
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  const [workspaceName, setWorkspaceName] = useState<string | null>(null);
  const [workspaceCloud, setWorkspaceCloud] = useState<string | null>(null);
  const [workspaceRegion, setWorkspaceRegion] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dal.lakehouse
      .listWorkspaces()
      .then((workspaces) => {
        if (workspaces.length > 0) {
          const ws = workspaces[0];
          setWorkspaceId(ws.id);
          setWorkspaceName(ws.name);
          setWorkspaceCloud(ws.cloud);
          setWorkspaceRegion(ws.region);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <WorkspaceContext.Provider
      value={{
        workspaceId,
        workspaceName,
        workspaceCloud,
        workspaceRegion,
        loading,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  return useContext(WorkspaceContext);
}

export function WorkspaceGuard({
  children,
}: {
  children: (workspaceId: string) => ReactNode;
}) {
  const { workspaceId, loading } = useWorkspace();
  if (loading) return <Loader />;
  if (!workspaceId)
    return (
      <Typography color='text.secondary'>No workspace available</Typography>
    );
  return <>{children(workspaceId)}</>;
}
